// auditpm-v6
import React, { useState, useEffect } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { supabase } from './lib/supabase'
import Layout from './components/Layout'
import Dashboard from './pages/Dashboard'
import Clients from './pages/Clients'
import Jobs from './pages/Jobs'
import MyAudits from './pages/MyAudits'
import { JobRoute, ExecuteRoute } from './components/JobRoute'
import IAWorksheet from './pages/IAWorksheet'
import IAForm from './pages/IAForm'
import IAReportDownload from './pages/IAReportDownload'
import IAReportPrint from './pages/IAReportPrint'
import IAPlanView from './pages/IAPlanView'
import OTEPrint from './pages/OTEPrint'
import OTEEndorsementDownload from './pages/OTEEndorsementDownload'
import IATermsPage from './pages/IATermsPage'
import OTETermsPage from './pages/OTETermsPage'
import OTEForm from './pages/OTEForm'
import OTEWorksheet from './pages/OTEWorksheet'
import Pipeline from './pages/Pipeline'
import Prospects from './pages/Prospects'
import Quotations from './pages/Quotations'
import Auditors from './pages/Auditors'
import Questionnaires from './pages/Questionnaires'
import ImpartialityRegister from './pages/ImpartialityRegister'
import Complaints from './pages/Complaints'
import RecordsRetention from './pages/RecordsRetention'
import InternalAudit from './pages/InternalAudit'
import ManagementReview from './pages/ManagementReview'
import TrainingProgramme from './pages/TrainingProgramme'
import PerformanceMonitoring from './pages/PerformanceMonitoring'
import ControlledDocuments from './pages/ControlledDocuments'
import AssessmentAccess from './pages/AssessmentAccess'
import AssessmentPortal from './pages/AssessmentPortal'
import Settings from './pages/Settings'
import Account from './pages/Account'
import RequireRole from './components/RequireRole'
import PublicForm from './pages/PublicForm'
import ClientAckForm from './pages/ClientAckForm'
import ClientReportAck from './pages/ClientReportAck'
import ReportPrint from './pages/ReportPrint'
import AuditorCOIForm from './pages/AuditorCOIForm'
import QuotationSign from './pages/QuotationSign'
import RenewalForm from './pages/RenewalForm'
import TermsPage from './pages/TermsPage'
import Login from './pages/Login'
import AuditTrail from './pages/AuditTrail'
import { AuthProvider, useAuth } from './lib/AuthContext'
import Toaster from './components/Toaster'
import { installAlertToast } from './lib/toast'
// Office font (self-hosted, OFL). Only downloaded where something uses it — the office shell (.layout), never
// the client pages or printed reports.
import '@fontsource/ibm-plex-sans/400.css'
import '@fontsource/ibm-plex-sans/500.css'
import '@fontsource/ibm-plex-sans/600.css'
import './App.css'

installAlertToast()   // safety net: a stray window.alert() shows as a message card

// Pure Auditor accounts land on My Audits; everyone else on the internal Dashboard.
function Home() {
  const { isAuditor, loading } = useAuth()
  if (loading) return <div style={{ color: '#888' }}>Loading…</div>
  return isAuditor ? <MyAudits /> : <Dashboard />
}

export default function App() {
  const [session, setSession] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session); setLoading(false)
    })
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_e, s) => setSession(s))
    return () => subscription.unsubscribe()
  }, [])

  if (loading) return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', color: '#888' }}>Loading…</div>

  return (
    <AuthProvider>
    <Toaster />
    <BrowserRouter>
      <Routes>
        <Route path="/form/:token" element={<PublicForm />} />
        <Route path="/renew/:token" element={<RenewalForm />} />
        <Route path="/ack/:token" element={<ClientAckForm />} />
        <Route path="/report-ack/:token" element={<ClientReportAck />} />
        <Route path="/report-print/:token" element={<ReportPrint />} />
        <Route path="/coi/:token" element={<AuditorCOIForm />} />
        <Route path="/assessment/:token" element={<AssessmentPortal />} />
        <Route path="/sign/:token" element={<QuotationSign />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/terms/internal-audit" element={<IATermsPage />} />
        <Route path="/terms/ote" element={<OTETermsPage />} />
        <Route path="/ote-form/:token" element={<OTEForm />} />
        <Route path="/ia/:token" element={<IAForm />} />
        <Route path="/ia-report/:token" element={<IAReportDownload />} />
        <Route path="/ia-report-print/:token" element={<IAReportPrint />} />
        <Route path="/ia-plan/:token" element={<IAPlanView />} />
        <Route path="/ote-print/:token" element={<OTEPrint />} />
        <Route path="/ote-endorsement/:token" element={<OTEEndorsementDownload />} />
        <Route path="/login" element={session ? <Navigate to="/" /> : <Login />} />
        <Route path="/*" element={
          session ? (
            <Layout>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/my-audits" element={<MyAudits />} />
                <Route path="/clients" element={<Clients />} />
                <Route path="/jobs" element={<Jobs />} />
                <Route path="/jobs/:id" element={<JobRoute />} />
                <Route path="/jobs/:id/execute" element={<ExecuteRoute />} />
                <Route path="/jobs/:id/ia" element={<IAWorksheet />} />
                <Route path="/jobs/:id/ote" element={<OTEWorksheet />} />
                <Route path="/pipeline" element={<Pipeline />} />
                <Route path="/prospects" element={<RequireRole allow="quote"><Prospects /></RequireRole>} />
                <Route path="/quotations" element={<Quotations />} />
                <Route path="/auditors" element={<Auditors />} />
                <Route path="/questionnaires" element={<RequireRole allow="approve"><Questionnaires /></RequireRole>} />
                <Route path="/impartiality" element={<ImpartialityRegister />} />
                <Route path="/complaints" element={<Complaints />} />
                <Route path="/records-retention" element={<RequireRole allow="approve"><RecordsRetention /></RequireRole>} />
                <Route path="/internal-audit" element={<RequireRole allow="approve"><InternalAudit /></RequireRole>} />
                <Route path="/management-review" element={<RequireRole allow="approve"><ManagementReview /></RequireRole>} />
                <Route path="/training-programme" element={<RequireRole allow="approve"><TrainingProgramme /></RequireRole>} />
                <Route path="/performance-monitoring" element={<RequireRole allow="approve"><PerformanceMonitoring /></RequireRole>} />
                <Route path="/controlled-documents" element={<ControlledDocuments />} />
                <Route path="/assessment-access" element={<RequireRole allow="approve"><AssessmentAccess /></RequireRole>} />
                <Route path="/settings" element={<RequireRole allow="admin"><Settings /></RequireRole>} />
                <Route path="/account" element={<Account />} />
                <Route path="/audit-trail" element={<AuditTrail />} />
              </Routes>
            </Layout>
          ) : <Navigate to="/login" />
        } />
      </Routes>
    </BrowserRouter>
  </AuthProvider>
  )
}
